import type { ReactNode } from 'react';
import { useState } from 'react';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, FileText, Pause, Play, Trash2 } from 'lucide-react';

import { ProFeatureLock } from '@/features/billing/ui/pro-feature-lock';
import {
  useAutomationRule,
  useAutomationLogs,
  useDeleteRule,
  useUpdateRule,
} from '@/features/automation';
import { AnalysisGuide } from '@/shared/ui/analysis-guide';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { DataErrorState } from '@/shared/ui/data-error-state';
import { DataTablePagination } from '@/shared/ui/data-table-pagination';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { TableCardHeader } from '@/shared/ui/table-card';
import { EmptyState } from '@/shared/ui/empty-state';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/shared/ui/alert-dialog';
import { AnimatedContainer } from '@/shared/ui/animated';
import { AppLayout } from '../-components/app-layout';

import type { AutomationTriggerType } from '@cafe24-apps/traffic-light-server/contracts';

export const Route = createFileRoute('/_authenticated/$mallId/automation/$ruleId')({
  component: AutomationRuleDetailPage,
});

const TRIGGER_TYPE_LABELS: Record<AutomationTriggerType, string> = {
  segment_change: '고객 상태 이동',
  ltv_cohort_change: 'LTV 등급 이동',
  overdue_entry: '구매주기 초과',
};

const SEGMENT_LABELS: Record<string, string> = {
  green: '활성',
  yellow: '주의',
  red: '이탈',
};

const COHORT_LABELS: Record<string, string> = {
  HIGH_VALUE: '우수 고객',
  GROWING: '성장 고객',
  AT_RISK: '이탈 위험',
  CHURNED: '이탈 고객',
};

const TRIGGER_GUIDES: Record<AutomationTriggerType, { title: string; tips: ReactNode[] }> = {
  segment_change: {
    title: '고객 상태 이동 트리거',
    tips: [
      <>
        고객의 RFM 상태가 <strong>이전 → 변경 후</strong>로 바뀔 때 쿠폰을 발급해요.
      </>,
      <>매일 새벽 분석이 돌면서 상태가 변한 고객을 자동 감지해요. 수동으로 실행할 필요가 없어요.</>,
      <>
        <strong>활성 → 주의</strong>로 떨어진 고객에게 리텐션 쿠폰을 보내면 이탈을 막을 수 있어요.
      </>,
    ],
  },
  ltv_cohort_change: {
    title: 'LTV 등급 이동 트리거',
    tips: [
      <>
        고객의 LTV 등급이 <strong>이전 → 변경 후</strong>로 바뀔 때 쿠폰을 발급해요.
      </>,
      <>
        LTV 등급은 누적 구매금액을 기반으로 매일 재계산돼요. 등급이 바뀌는 순간 자동으로 작동해요.
      </>,
      <>
        <strong>성장 고객 → 이탈 위험</strong>으로 떨어진 고객은 이탈 직전이에요. 빠르게 쿠폰을
        보내면 재방문 확률이 높아져요.
      </>,
    ],
  },
  overdue_entry: {
    title: '구매주기 초과 트리거',
    tips: [
      <>고객의 평균 구매 주기를 넘기면 자동으로 쿠폰을 발급해요.</>,
      <>
        예를 들어 재구매 주기가 30일인 고객이 35일째 구매하지 않았다면, 리마인더 쿠폰이
        효과적이에요.
      </>,
      <>
        별도의 이전/변경 후 설정 없이, <strong>구매주기 분석</strong> 결과를 바탕으로 대상을 자동
        감지해요.
      </>,
    ],
  },
};

function TriggerConfigBadges({
  rule,
}: {
  rule: { triggerType: AutomationTriggerType; triggerConfig: Record<string, unknown> };
}) {
  const config = rule.triggerConfig;

  if (rule.triggerType === 'segment_change') {
    const from = (config.fromSegments as string[]) ?? [];
    const to = (config.toSegments as string[]) ?? [];
    if (from.length === 0 && to.length === 0) return null;
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1">
          {from.map((s) => (
            <Badge key={s} variant="secondary" className="text-xs">
              {SEGMENT_LABELS[s] ?? s}
            </Badge>
          ))}
        </div>
        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <div className="flex gap-1">
          {to.map((s) => (
            <Badge key={s} variant="secondary" className="text-xs">
              {SEGMENT_LABELS[s] ?? s}
            </Badge>
          ))}
        </div>
      </div>
    );
  }

  if (rule.triggerType === 'ltv_cohort_change') {
    const from = (config.fromCohorts as string[]) ?? [];
    const to = (config.toCohorts as string[]) ?? [];
    if (from.length === 0 && to.length === 0) return null;
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1">
          {from.map((c) => (
            <Badge key={c} variant="secondary" className="text-xs">
              {COHORT_LABELS[c] ?? c}
            </Badge>
          ))}
        </div>
        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <div className="flex gap-1">
          {to.map((c) => (
            <Badge key={c} variant="secondary" className="text-xs">
              {COHORT_LABELS[c] ?? c}
            </Badge>
          ))}
        </div>
      </div>
    );
  }

  return <p className="text-xs text-muted-foreground">구매주기 분석 결과를 바탕으로 자동 감지</p>;
}

function AutomationRuleDetailPage() {
  const { mallId, ruleId } = useParams({ from: '/_authenticated/$mallId/automation/$ruleId' });
  const navigate = useNavigate();
  const [logPage, setLogPage] = useState(1);
  const [logLimit, setLogLimit] = useState(10);

  const { data: rule, isLoading: ruleLoading, isError: ruleError } = useAutomationRule(ruleId!);
  const {
    data: logsData,
    isLoading: logsLoading,
    isError: logsError,
  } = useAutomationLogs({
    ruleId: ruleId!,
    page: logPage,
    limit: logLimit,
  });
  const { mutate: updateRule } = useUpdateRule();
  const { mutate: deleteRule, isPending: isDeleting } = useDeleteRule();

  const logs = logsData?.data ?? [];
  const logPagination = logsData?.pagination;

  const handleDelete = () => {
    deleteRule(
      { ruleId: ruleId! },
      {
        onSuccess: () => {
          navigate({ to: '/$mallId/automation', params: { mallId: mallId } });
        },
      },
    );
  };

  return (
    <AppLayout pageTitle="자동화">
      <ProFeatureLock feature="couponAutoIssueEnabled">
        <div className="max-w-4xl mx-auto space-y-6">
          <AnimatedContainer delay={0}>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" asChild>
                <Link to="/$mallId/automation" params={{ mallId: mallId }}>
                  <ArrowLeft className="w-4 h-4" />
                  목록
                </Link>
              </Button>
              {ruleLoading ? (
                <Skeleton className="h-7 w-48" />
              ) : (
                <h2 className="text-lg font-semibold">{rule?.name}</h2>
              )}
            </div>
          </AnimatedContainer>

          {!ruleLoading && ruleError ? (
            <AnimatedContainer delay={0.1}>
              <Card>
                <DataErrorState
                  title="규칙 정보를 불러오지 못했어요"
                  description="잠시 후 다시 시도해 주세요"
                />
              </Card>
            </AnimatedContainer>
          ) : ruleLoading ? (
            <AnimatedContainer delay={0.1}>
              <Card className="py-6 px-6">
                <div className="space-y-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-6 w-full" />
                  ))}
                </div>
              </Card>
            </AnimatedContainer>
          ) : rule ? (
            <>
              <AnimatedContainer delay={0.1}>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">규칙 정보</CardTitle>
                    <CardAction>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          onClick={() => updateRule({ ruleId: rule.id, enabled: !rule.enabled })}
                        >
                          {rule.enabled ? (
                            <>
                              <Pause className="w-3.5 h-3.5" />
                              비활성화
                            </>
                          ) : (
                            <>
                              <Play className="w-3.5 h-3.5" />
                              활성화
                            </>
                          )}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm">
                              <Trash2 className="w-3.5 h-3.5 mr-1" />
                              삭제
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>규칙을 삭제할까요?</AlertDialogTitle>
                              <AlertDialogDescription>
                                "{rule.name}" 규칙과 실행 로그가 모두 삭제돼요. 이 작업은 되돌릴 수
                                없어요.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>취소</AlertDialogCancel>
                              <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
                                삭제
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </CardAction>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <dl className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <dt className="text-muted-foreground">상태</dt>
                        <dd className="mt-1">
                          <Badge variant={rule.enabled ? 'default' : 'secondary'}>
                            {rule.enabled ? '활성' : '비활성'}
                          </Badge>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">트리거</dt>
                        <dd className="mt-1">
                          <Badge variant="outline">{TRIGGER_TYPE_LABELS[rule.triggerType]}</Badge>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">쿠폰</dt>
                        <dd className="mt-1">{rule.cafe24CouponName}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">총 발급</dt>
                        <dd className="mt-1 text-xl font-bold tabular-nums">
                          {rule.totalIssued.toLocaleString()}
                          <span className="text-sm font-normal text-muted-foreground ml-0.5">
                            건
                          </span>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">쿨다운</dt>
                        <dd className="mt-1 tabular-nums">{rule.cooldownDays}일</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">최근 발급 제외</dt>
                        <dd className="mt-1 tabular-nums">{rule.excludeRecentDays}일</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">회원당 최대</dt>
                        <dd className="mt-1 tabular-nums">{rule.maxIssuesPerMember}회</dd>
                      </div>
                    </dl>

                    {/* 트리거 조건 시각화 */}
                    <div className="pt-4 border-t border-border/50">
                      <p className="text-xs text-muted-foreground mb-2">발동 조건</p>
                      <TriggerConfigBadges rule={rule} />
                    </div>
                  </CardContent>
                </Card>
              </AnimatedContainer>

              {/* 트리거 가이드 */}
              <AnimatedContainer delay={0.15}>
                <AnalysisGuide title={TRIGGER_GUIDES[rule.triggerType].title}>
                  <ul className="space-y-1 list-disc list-inside">
                    {TRIGGER_GUIDES[rule.triggerType].tips.map((tip, i) => (
                      <li key={i}>{tip}</li>
                    ))}
                  </ul>
                </AnalysisGuide>
              </AnimatedContainer>

              <AnimatedContainer delay={0.2}>
                <Card className="py-0 gap-0">
                  <TableCardHeader title="실행 로그" />
                  {!logsLoading && logsError ? (
                    <DataErrorState />
                  ) : !logsLoading && logs.length === 0 ? (
                    <EmptyState
                      icon={FileText}
                      title="아직 실행 기록이 없어요"
                      description="규칙이 활성 상태이면 조건에 맞는 고객이 생길 때 자동으로 실행돼요"
                    />
                  ) : (
                    <>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>실행 시각</TableHead>
                            <TableHead>쿠폰 이벤트</TableHead>
                            <TableHead className="text-right">대상</TableHead>
                            <TableHead className="text-right">발급</TableHead>
                            <TableHead className="text-right">스킵</TableHead>
                            <TableHead className="text-right">실패</TableHead>
                            <TableHead>에러</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {logsLoading
                            ? Array.from({ length: 3 }).map((_, i) => (
                                <TableRow key={i}>
                                  {Array.from({ length: 7 }).map((__, j) => (
                                    <TableCell key={j}>
                                      <Skeleton className="h-5 w-16" />
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))
                            : logs.map((log) => (
                                <TableRow key={log.id}>
                                  <TableCell className="text-sm tabular-nums">
                                    {new Date(log.triggeredAt).toLocaleString('ko-KR')}
                                  </TableCell>
                                  <TableCell className="text-sm max-w-48 truncate">
                                    {log.couponEventId ? (
                                      <Link
                                        to="/$mallId/coupon/$eventId"
                                        params={{ mallId: mallId, eventId: log.couponEventId }}
                                        className="text-foreground underline underline-offset-2 hover:opacity-70"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {log.couponEventName ?? '이벤트 상세'}
                                      </Link>
                                    ) : (
                                      <span className="text-muted-foreground">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums">
                                    {log.targetCount}
                                  </TableCell>
                                  <TableCell className="text-right text-signal-green-text font-medium tabular-nums">
                                    {log.issuedCount}
                                  </TableCell>
                                  <TableCell className="text-right text-muted-foreground tabular-nums">
                                    {log.skippedCount}
                                  </TableCell>
                                  <TableCell className="text-right text-destructive tabular-nums">
                                    {log.failedCount > 0 ? log.failedCount : '-'}
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground max-w-40 truncate">
                                    {log.errorMessage ?? '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                        </TableBody>
                      </Table>
                      {logPagination && logPagination.total > 0 && (
                        <DataTablePagination
                          page={logPage}
                          limit={logLimit}
                          total={logPagination.total}
                          totalPages={Math.ceil(logPagination.total / logLimit)}
                          onPageChange={setLogPage}
                          onLimitChange={(l) => {
                            setLogLimit(l);
                            setLogPage(1);
                          }}
                        />
                      )}
                    </>
                  )}
                </Card>
              </AnimatedContainer>
            </>
          ) : null}
        </div>
      </ProFeatureLock>
    </AppLayout>
  );
}
